import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage keys
const TOKEN_KEY = 'calvio_session_token';
const USER_KEY = 'calvio_user';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Create axios instance with default config
const authAxios = axios.create({
  baseURL: API,
  withCredentials: true
});

// Add interceptor to include token in headers
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage if available
    const savedUser = localStorage.getItem(USER_KEY);
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 500; // ms
    
    const token = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    
    // If no token stored, user is not authenticated
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    // If we have a token and saved user, set user immediately
    // This allows the UI to render while we verify the session
    let parsedUser = null;
    if (savedUser) {
      try {
        parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch (e) {
        // Invalid JSON, clear it
        localStorage.removeItem(USER_KEY);
      }
    }
    
    try {
      const response = await authAxios.get('/auth/me');
      // Update user with fresh data from server
      setUser(response.data);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      setLoading(false);
    } catch (error) {
      console.error('Auth check failed:', error, 'Retry:', retryCount);
      
      // Only clear if it's definitely an auth error (401)
      if (error.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setLoading(false);
      } else if (retryCount < MAX_RETRIES) {
        // For network errors or other issues, retry after a delay
        console.log(`Retrying auth check (${retryCount + 1}/${MAX_RETRIES})...`);
        setTimeout(() => {
          checkAuth(retryCount + 1);
        }, RETRY_DELAY);
      } else {
        // Max retries reached - if we have cached user, use it
        if (parsedUser) {
          console.log('Using cached user after retries exhausted');
          setLoading(false);
        } else {
          // No cached user and network error - clear auth state
          setUser(null);
          setLoading(false);
        }
      }
    }
  };

  const login = async (userData, sessionToken) => {
    // Store token and user data
    if (sessionToken) {
      localStorage.setItem(TOKEN_KEY, sessionToken);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
  };

  const updateUser = (updatedData) => {
    const newUser = { ...user, ...updatedData };
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setUser(newUser);
  };

  const logout = async () => {
    try {
      await authAxios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear stored data
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    checkAuth,
    authAxios // Export for use in other components
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export authAxios for use without context
export { authAxios };
